import { logoLoader } from '@/utils/imagepath';
import React, {
  createContext,
  useState,
  useCallback,
  useContext,
  useEffect,
} from 'react';

interface SpinnerContextType {
  isLoading: boolean;
  isTableLoading: boolean;
  showLoaderInner: () => void;
  hideLoaderInner: () => void;
  showTableLoader: () => void;
  hideTableLoader: () => void;
  isLoaderActive: (id: any) => boolean;
  hideLoaderById: (id: any) => void;
  showLoaderById: (id: string) => void;
}

interface Props {
  children: React.ReactNode;
}
export const LoaderComponent = ({ loaderMsg = '' }) => {
  return (
    <div className="loader-main">
      <span className="page-loader">
        <img src={logoLoader} alt="Loader" />
      </span>
      {/* <div className="loader-logo">
        <img src={logoLoader} alt="Logo" />
      </div> */}
      {loaderMsg && (
        <div className="loader-content">
          <h6>{loaderMsg}</h6>
        </div>
      )}
    </div>
  );
};
export const SpinnerContext = createContext<SpinnerContextType>({
  isLoading: false,
  isTableLoading: false,
  showLoaderInner: () => {},
  hideLoaderInner: () => {},
  showTableLoader: () => {},
  hideTableLoader: () => {},
  hideLoaderById: (id: string) => {},
  isLoaderActive: (id: string) => false,
  showLoaderById: (id: string) => {},
});

export const useSpinner = () => useContext(SpinnerContext);

let externalShowLoader: (msg: string) => void;
let externalHideLoader: (msg: string) => void;
let externalShowTableLoader: () => void;
let externalHideTableLoader: () => void;
let externalShowLoaderById: (id: string) => void;

let externalHideLoaderById: (id: string) => void;
let externalIsActiveLoaderById: (id: string) => boolean;

const SpinnerProvider: React.FC<Props> = ({ children }) => {
  const [loaderCount, setLoaderCount] = useState(1);
  const [loaderMsg, setLoaderMsg] = useState('');
  const [isTableLoading, setTableLoading] = useState(false);
  const [activeLoaders, setActiveLoaders] = useState<any>({});
  // ------------------testing

  const showLoaderById = useCallback((id: string) => {
    setActiveLoaders((prev: any) => ({ ...prev, [id]: true }));
  }, []);

  const hideLoaderById = useCallback((id: string) => {
    setActiveLoaders((prev: any) => {
      const updated = { ...prev };
      delete updated[id]; // Dynamically remove the loader for the given ID
      return updated;
    });
  }, []);

  const isLoaderActive = useCallback(
    (id: string) => {
      return !!activeLoaders[id] || false;
    },
    [activeLoaders]
  );

  const showLoaderInner = useCallback((msg = '') => {
    msg && setLoaderMsg(msg);
    setLoaderCount((prevCount) => prevCount + 1);
  }, []);

  const hideLoaderInner = useCallback((msg = '') => {
    setTimeout(() => {
      if (msg === 'video') {
        setLoaderCount(0);
      } else if (msg === 'register') {
        setLoaderCount(0);
      } else {
        setLoaderCount((prevCount) => (prevCount > 0 ? prevCount - 1 : 0));
      }
      setLoaderMsg('');
    }, 500);
  }, []);

  const showTableLoader = useCallback(() => {
    setTableLoading(true);
  }, []);

  const hideTableLoader = useCallback(() => {
    setTableLoading(false);
  }, []);

  externalShowLoader = showLoaderInner;
  externalHideLoader = hideLoaderInner;
  externalShowTableLoader = showTableLoader;
  externalHideTableLoader = hideTableLoader;
  externalShowLoaderById = showLoaderById;
  externalHideLoaderById = hideLoaderById;
  externalIsActiveLoaderById = isLoaderActive;

  useEffect(() => {
    hideLoaderInner();
    setActiveLoaders({});
  }, []);

  return (
    <SpinnerContext.Provider
      value={{
        isLoading: loaderCount > 0,
        isTableLoading,
        showLoaderInner,
        hideLoaderInner,
        showTableLoader,
        hideTableLoader,
        isLoaderActive,
        showLoaderById,
        hideLoaderById,
      }}
    >
      {children}

      {loaderCount > 0 && <LoaderComponent loaderMsg={loaderMsg} />}
    </SpinnerContext.Provider>
  );
};

export const showLoader = (msg = '') =>
  externalShowLoader && externalShowLoader(msg);
export const hideLoader = (msg = '') =>
  externalHideLoader && externalHideLoader(msg);

export const showTableLoader = () =>
  externalShowTableLoader && externalShowTableLoader();
export const hideTableLoader = () =>
  externalHideTableLoader && externalHideTableLoader();

export const showLoaderById = (id: string) =>
  externalShowLoaderById && externalShowLoaderById(id);

export const hideLoaderById = (id: string) =>
  externalHideLoaderById && externalHideLoaderById(id);
export const isActiveLoader = (id: string) =>
  externalIsActiveLoaderById && externalIsActiveLoaderById(id);

export default SpinnerProvider;
